import { Timestamp } from "firebase/firestore";

/**
 * Convert Firestore Timestamps to Date objects recursively
 */
function convertTimestamps(data: any): any {
  if (data === null || data === undefined) {
    return data;
  }

  // Handle Firestore Timestamp from client SDK
  if (data instanceof Timestamp) {
    return data.toDate();
  }

  // Handle Firestore Timestamp from admin SDK (_seconds and _nanoseconds)
  if (
    typeof data === "object" &&
    "_seconds" in data &&
    "_nanoseconds" in data
  ) {
    return new Date(data._seconds * 1000 + data._nanoseconds / 1000000);
  }

  // Handle serialized timestamps with __timestamp__ property
  if (
    typeof data === "object" &&
    "__timestamp__" in data &&
    typeof data.__timestamp__ === "string"
  ) {
    return new Date(data.__timestamp__);
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map(convertTimestamps);
  }

  // Handle objects
  if (typeof data === "object" && !(data instanceof Date)) {
    const converted: Record<string, any> = {};
    for (const key in data) {
      converted[key] = convertTimestamps(data[key]);
    }
    return converted;
  }

  return data;
}

/**
 * Helper function to format Firestore document data
 */
export const formatFirestoreDocument = (
  doc:
    | {
        id: string;
        data: () => Record<string, unknown>;
      }
    | FirebaseFirestore.DocumentSnapshot,
) => {
  // Convert Firestore Timestamps to Date objects for serialization.
  const data = doc.data();

  // Return formatted document with ID included.
  return {
    id: doc.id,
    ...convertTimestamps(data),
  };
};

/**
 * Clean data for Firestore by converting undefined to null
 * Firestore doesn't accept undefined values, but accepts null
 */
export function cleanForFirestore<T extends Record<string, unknown>>(
  obj: T,
): T {
  const result: Record<string, unknown> = {};

  for (const key in obj) {
    const value = obj[key];

    // Convert undefined to null
    if (value === undefined) {
      result[key] = null;
      continue;
    }

    if (
      value !== null &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      !(value instanceof Date)
    ) {
      // Recursively clean nested objects
      result[key] = cleanForFirestore(value as Record<string, unknown>);
    } else {
      result[key] = value;
    }
  }

  return result as T;
}
